import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Confirm, Empty, Icon, Loading } from '../components/ui';
import { RewardedAdPlayer, useRewardedAd } from '../components/RewardedAd';
import { useT } from '../i18n';
import { stopMusic } from '../lib/sound';
import { useGame } from '../store/game';
import { ThemeToggle } from '../components/ThemeToggle';
import { saveSettings, useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { CallBreakTable } from './CallBreakTable';
import { SpadesTable } from './SpadesTable';
import { TarneebTable } from './TarneebTable';
import { DehlaPakadTable } from './DehlaPakadTable';
import { HeartsTable } from './HeartsTable';
import { FiveHundredTable } from './FiveHundredTable';
import { JudgementTable } from './JudgementTable';
import { RummyTable } from './RummyTable';
import { EuchreTable } from './EuchreTable';
import { GinTable } from './GinTable';
import { TwentyNineTable } from './TwentyNineTable';
import { CrazyEightsTable } from './CrazyEightsTable';
import { CribbageTable } from './CribbageTable';
import { BlackjackTable } from './BlackjackTable';
import { BhabhiTable } from './BhabhiTable';
import { DurakTable } from './DurakTable';
import { HazariTable } from './HazariTable';
import { PresidentTable } from './PresidentTable';
import { BigTwoTable } from './BigTwoTable';
import { GoFishTable } from './GoFishTable';
import { WarTable } from './WarTable';
import { PinochleTable } from './PinochleTable';
import { BridgeTable } from './BridgeTable';
import { SevensTable } from './SevensTable';
import { ScopaTable } from './ScopaTable';
import { SeepTable } from './SeepTable';
import { WhistTable } from './WhistTable';
import { CanastaTable } from './CanastaTable';
import { Rummy500Table } from './Rummy500Table';
import { SkatTable } from './SkatTable';
import { BeloteTable } from './BeloteTable';
import { PitchTable } from './PitchTable';
import { BriscolaTable } from './BriscolaTable';
import { TressetteTable } from './TressetteTable';
import { BluffTable } from './BluffTable';
import { CourtPieceTable } from './CourtPieceTable';
import { BaccaratTable } from './BaccaratTable';
import { DragonTigerTable } from './DragonTigerTable';
import { ThreeCardPokerTable } from './ThreeCardPokerTable';
import { PaiGowTable } from './PaiGowTable';
import { CasinoWarTable } from './CasinoWarTable';
import { RedDogTable } from './RedDogTable';
import { AndarBaharTable } from './AndarBaharTable';
import { maybeShowInterstitial } from '../native/ads';
import { api } from '../api/client';
import { OFFLINE_TABLE_ID, isOfflineTable, restartOfflineTable } from '../offline/offline-game';
import { ChatPanel, HandResultFlash, MatchResults } from './common';
import { MarriageTable } from './MarriageTable';
import { PokerTable } from './PokerTable';
import { TeenPattiTable } from './TeenPattiTable';

/** Fixed-length competitions (entry fee, placement payouts) as opposed to chip-based session games. */
export const isMatchGame = (g: string | undefined): boolean => g === 'CALL_BREAK' || g === 'SPADES' || g === 'TARNEEB' || g === 'HEARTS' || g === 'FIVE_HUNDRED' || g === 'JUDGEMENT' || g === 'RUMMY' || g === 'EUCHRE' || g === 'GIN_RUMMY' || g === 'TWENTY_NINE' || g === 'CRAZY_EIGHTS' || g === 'CRIBBAGE' || g === 'BHABHI' || g === 'PINOCHLE' || g === 'BRIDGE' || g === 'SEVENS' || g === 'SCOPA' || g === 'BLUFF' || g === 'COURT_PIECE' || g === 'DEHLA_PAKAD' || g === 'WHIST' || g === 'SEEP' || g === 'BELOTE' || g === 'DURAK' || g === 'PRESIDENT' || g === 'BRISCOLA' || g === 'GO_FISH' || g === 'TRESSETTE' || g === 'CANASTA' || g === 'SKAT' || g === 'BIG_TWO' || g === 'WAR' || g === 'RUMMY_500' || g === 'PITCH' || g === 'HAZARI';

/** Waiting for the table to arrive. It never spins forever: a local game that is gone, or a table that is slow, offers a way out. */
function TableLoading({ local, onLeave }: { local: boolean; onLeave: () => void }) {
  const t = useT();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const h = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(h);
  }, []);
  const gone = local || slow;
  return (
    <div className="table-screen" style={{ display: 'grid', placeItems: 'center' }}>
      {local ? <Empty emoji="👋" title="This game has ended" body="Offline games can't be resumed after the app reloads." action={<button className="btn primary" onClick={onLeave}>{t('results.lobby')}</button>} /> : (
        <div className="col" style={{ alignItems: 'center' }}>
          <Loading />
          {gone && <button className="btn" onClick={onLeave}>{t('results.lobby')}</button>}
        </div>
      )}
    </div>
  );
}

export function TableScreen({ spectate = false }: { spectate?: boolean }) {
  const { tableId = '' } = useParams();
  const t = useT();
  const nav = useNavigate();
  const table = useGame((s) => s.table);
  const view = useGame((s) => s.view);
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const result = useGame((s) => s.result);
  const closed = useGame((s) => s.closed);
  const open = useGame((s) => s.open);
  const leave = useGame((s) => s.leave);
  const imBack = useGame((s) => s.imBack);
  const quickPlay = useGame((s) => s.quickPlay);
  const reset = useGame((s) => s.reset);
  const clearResult = useGame((s) => s.clearResult);
  const ws = useSession((s) => s.ws);
  const sound = useSettings((s) => s.settings.sound);
  const [chatOpen, setChatOpen] = useState(false);
  const [scoresOpen, setScoresOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const bonusAd = useRewardedAd('results_bonus');

  useEffect(() => {
    stopMusic();
    if (ws === 'open' && table?.tableId !== tableId) void open(tableId, spectate);
  }, [tableId, ws, table?.tableId, open, spectate]);

  // The browser/phone back button (and swipe-back) must ask before walking away from a game in progress.
  const guarded = mySeat !== null && table?.tableId === tableId && !closed && !(isMatchGame(table?.gameType) && result);
  useEffect(() => {
    if (!guarded) return;
    window.history.pushState({ tableGuard: true }, '');
    const onPop = () => {
      window.history.pushState({ tableGuard: true }, '');
      setConfirmLeave(true);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [guarded]);

  // Keep the screen awake during play where supported.
  useEffect(() => {
    let lock: { release(): Promise<void> } | null = null;
    const nav2 = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    nav2.wakeLock?.request('screen').then((l) => (lock = l)).catch(() => undefined);
    return () => void lock?.release().catch(() => undefined);
  }, []);

  const me = mySeat !== null ? seats[mySeat] : undefined;
  const watching = mySeat === null;
  const kind = isMatchGame(table?.gameType) ? 'match' : 'session';

  if (closed && (!table || closed.tableId === table.tableId) && !result) {
    return (
      <div className="table-screen" style={{ display: 'grid', placeItems: 'center' }}>
        <Empty
          emoji="👋"
          title={closed.reason === 'insufficient_chips' ? 'Out of chips — you left the table' : closed.reason === 'TABLE_CLOSED' ? 'This table has ended' : closed.reason === 'auto_play' ? 'You were away — auto-play finished the game and you left the table' : 'You left the table'}
          body={closed.chips !== undefined ? `${closed.chips} chips returned to your wallet.` : undefined}
          action={
            <button className="btn primary" onClick={() => { reset(); nav('/'); }}>
              {t('results.lobby')}
            </button>
          }
        />
      </div>
    );
  }
  if (!table || table.tableId !== tableId || !view) {
    return <TableLoading local={tableId === OFFLINE_TABLE_ID} onLeave={() => { reset(); nav('/', { replace: true }); }} />;
  }

  const interstitial = () => {
    if (isOfflineTable(table.tableId)) return;
    void api
      .get<{ placements: { ad_type: string; placement: string; enabled: boolean; frequency: number }[] }>('/api/v1/ads/config')
      .then((c) => {
        const p = c.placements.find((x) => x.ad_type === 'INTERSTITIAL' && x.placement === 'between_matches' && x.enabled);
        if (p) void maybeShowInterstitial(p.frequency);
      })
      .catch(() => undefined);
  };

  const offlineGame = isOfflineTable(table.tableId);
  const playAgain = () => {
    if (offlineGame) {
      clearResult();
      restartOfflineTable();
      return;
    }
    interstitial();
    const cfg = table.tableConfigId;
    const isPrivate = table.privacy === 'PRIVATE';
    clearResult();
    reset();
    if (isPrivate && table.roomCode) nav(`/rooms/${table.roomCode}`);
    else {
      nav('/');
      void quickPlay(cfg);
    }
  };

  return (
    <div className="table-screen" data-game={table.gameType}>
      <header className="table-top">
        <button className="icon-btn" onClick={() => (watching ? void leave().then(() => nav(-1)) : kind === 'match' && result ? (reset(), nav('/')) : setConfirmLeave(true))} aria-label={t('table.leave')}>
          <Icon name="back" />
        </button>
        <div className="grow">
          <div className="title ellipsis">{table.name}</div>
          <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
            {t(`game.${table.gameType}` as 'game.CALL_BREAK')}
            {table.roomCode ? ` · ${t('rooms.code')} ${table.roomCode}` : ''}
          </div>
        </div>
        {(watching || me?.botControlled) && (
          <div className="table-chip" role="status">
            <span className="ellipsis">{watching ? `👁 ${t('watch.banner')}` : t('table.autoPlay')}</span>
            {!watching && (
              <button className="btn sm gold" onClick={imBack}>
                {t('table.imBack')}
              </button>
            )}
          </div>
        )}
        {isMatchGame(table.gameType) && (
          <button className="icon-btn" onClick={() => setScoresOpen(true)} aria-label={t('table.scores')}>
            <Icon name="trophy" />
          </button>
        )}
        <ThemeToggle />
        <button className="icon-btn" onClick={() => void saveSettings({ sound: !sound })} aria-label={t('settings.sound')} aria-pressed={sound}>
          {sound ? '🔊' : '🔇'}
        </button>
        <button className="icon-btn" onClick={() => setChatOpen((v) => !v)} aria-label={t('table.chat')} aria-expanded={chatOpen}>
          <Icon name="chat" />
        </button>
      </header>

      <div className="table-area">
        {table.gameType === 'CALL_BREAK' && <CallBreakTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'SPADES' && <SpadesTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'DEHLA_PAKAD' && <DehlaPakadTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'TARNEEB' && <TarneebTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'HEARTS' && <HeartsTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'FIVE_HUNDRED' && <FiveHundredTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'JUDGEMENT' && <JudgementTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'RUMMY' && <RummyTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'EUCHRE' && <EuchreTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'GIN_RUMMY' && <GinTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'TWENTY_NINE' && <TwentyNineTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'CRAZY_EIGHTS' && <CrazyEightsTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'CRIBBAGE' && <CribbageTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {(table.gameType === 'TEEN_PATTI' || table.gameType === 'FLUSH' || table.gameType === 'BRAG') && <TeenPattiTable />}
        {table.gameType === 'BLACKJACK' && <BlackjackTable />}
        {table.gameType === 'ANDAR_BAHAR' && <AndarBaharTable />}
        {table.gameType === 'BACCARAT' && <BaccaratTable />}
        {table.gameType === 'DRAGON_TIGER' && <DragonTigerTable />}
        {table.gameType === 'THREE_CARD_POKER' && <ThreeCardPokerTable />}
        {table.gameType === 'PAI_GOW' && <PaiGowTable />}
        {table.gameType === 'CASINO_WAR' && <CasinoWarTable />}
        {table.gameType === 'RED_DOG' && <RedDogTable />}
        {table.gameType === 'BHABHI' && <BhabhiTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'DURAK' && <DurakTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'HAZARI' && <HazariTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'PRESIDENT' && <PresidentTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'BIG_TWO' && <BigTwoTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'WAR' && <WarTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'GO_FISH' && <GoFishTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'PINOCHLE' && <PinochleTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'BRIDGE' && <BridgeTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'SEVENS' && <SevensTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'SCOPA' && <ScopaTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'SEEP' && <SeepTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'WHIST' && <WhistTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'CANASTA' && <CanastaTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'RUMMY_500' && <Rummy500Table scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'SKAT' && <SkatTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'BELOTE' && <BeloteTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'PITCH' && <PitchTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'BRISCOLA' && <BriscolaTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'TRESSETTE' && <TressetteTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'BLUFF' && <BluffTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'COURT_PIECE' && <CourtPieceTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {table.gameType === 'POKER' && <PokerTable />}
        {table.gameType === 'MARRIAGE' && <MarriageTable />}
        {kind === 'session' && <HandResultFlash result={result} />}
        <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
      </div>
      <div />

      {kind === 'match' && result && (
        <MatchResults
          result={result}
          onPlayAgain={playAgain}
          onLobby={() => {
            interstitial();
            reset();
            nav('/');
          }}
        />
      )}
      {kind === 'match' && result && !offlineGame && useSession.getState().boot?.flags.REWARDED_ADS_ENABLED && !bonusAd.session && (
        <button className="btn gold" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 120 }} onClick={() => void bonusAd.start()}>
          📺 {t('results.bonusAd', { n: 300 })}
        </button>
      )}
      {bonusAd.session && <RewardedAdPlayer session={bonusAd.session} onClose={() => bonusAd.close()} />}
      <Confirm
        open={confirmLeave}
        title={t('table.leaveConfirm')}
        body={kind === 'match' ? t('table.leaveMatchWarn') : t('table.leaveSessionWarn')}
        confirmText={t('table.leave')}
        danger
        onCancel={() => setConfirmLeave(false)}
        onConfirm={() => {
          setConfirmLeave(false);
          // replace: drops the guard entry so Back from the lobby doesn't land on this table again.
          void leave().then(() => nav('/', { replace: true }));
        }}
      />
    </div>
  );
}
